// Loads a single training by key for the reader: fetches every readable version of the key
// (with the markdown `content` body), resolves the live version client-side, and confirms it
// is in-scope for the current rooftop — so a link to a module the rooftop can't see (or one
// that is no longer live) resolves to `undefined` and the page shows a friendly message.

import { useMemo } from 'react';
import { useGetLearningModuleByKeyQuery } from '../queries/getLearningModules.query.generated';
import { resolveLiveModules, filterForRooftop } from '../utils/resolveModules';
import { useDealerScope } from './useDealerScope';
import type { LearningModule } from '../types';

export function useLearningModule(key: string | undefined) {
  const { scope, ready: scopeReady } = useDealerScope();
  const { data, loading, error, refetch } = useGetLearningModuleByKeyQuery({
    variables: { key: key ?? '' },
    skip: !key,
    fetchPolicy: 'cache-and-network',
  });

  const versions = useMemo<LearningModule[]>(
    () => (data?.learning_modules ?? []) as unknown as LearningModule[],
    [data],
  );

  const module = useMemo<LearningModule | undefined>(() => {
    if (!scope || versions.length === 0) return undefined;
    const live = resolveLiveModules(versions, new Date());
    const head = live.find(m => m.key === key);
    if (!head) return undefined;
    return filterForRooftop([head], scope).length > 0 ? head : undefined;
  }, [versions, scope, key]);

  // Scope hydration is part of "loading": module=undefined with a pending rooftop must render
  // as a spinner, not as the definitive "This training isn't available."
  return { module, loading: loading || !scopeReady, error, refetch };
}
